import {
  asOperatorDescriptor,
  BookingDto,
  extractPassengerTypeIdsFromPassengerCriteria,
  extractSectorsFromProductionCriteria,
  FormatDateTimeNormalizedFn,
  IProduceJoinPassengerPreviewDto,
  IProductionFiltersMetadata,
  IRichLogData,
  isValidDateTime,
  ITypeormWhereCriteria,
  joinDirectionInValues,
  NO_VALUE_DISPLAY,
  PROVIDER_OPTIONS,
  RydeUiUnit,
} from "@ryde/shared";

import {
  PRODUCTION_LOGS_JOIN_DELIMITER,
  PRODUCTION_METRICS_FILTER_ID_KEYS,
} from "./production-logs.constants";
import type {
  IIdFilterValue,
  IIdsOnlyFilterValue,
  ResolvedFilterValue,
  TProductionMetricsFilterDisplayItem,
} from "./production-logs.models";

export interface IResolveProductionMetricsFiltersDeps {
  getDropdownLabel: (entity: RydeUiUnit, id: number) => Promise<string>;
  getDropdownLabels: (entity: RydeUiUnit, ids: number[]) => Promise<string[]>;
  t: (key: string) => string;
  formatDateTimeNormalized: FormatDateTimeNormalizedFn;
}

type IProductionFiltersMetadataSource = IProductionFiltersMetadata & {
  criteria?: ITypeormWhereCriteria<BookingDto> &
    IProduceJoinPassengerPreviewDto;
};

type IProductionCriteria = NonNullable<
  IProductionFiltersMetadataSource["criteria"]
>;

const hasBookingIdsInProductionFilters = (
  filters: IProductionFiltersMetadata,
): boolean => (filters.bookingIds?.length ?? 0) > 0;

interface IFormatProductionMetricsDateParams {
  dateIso: TDateISO | undefined;
  formatDateTimeNormalized: FormatDateTimeNormalizedFn;
}

const formatProductionMetricsDate = ({
  dateIso,
  formatDateTimeNormalized,
}: IFormatProductionMetricsDateParams): string => {
  if (!dateIso || !isValidDateTime(dateIso)) {
    return NO_VALUE_DISPLAY;
  }

  return formatDateTimeNormalized(dateIso);
};

export const normalizeProductionFiltersMetadata = (
  filters: IProductionFiltersMetadataSource | undefined,
): IProductionFiltersMetadata | undefined => {
  if (!filters) {
    return undefined;
  }

  const { criteria, ...rest } = filters;
  const normalized: IProductionFiltersMetadata = { ...rest };

  if (!criteria) {
    return normalized;
  }

  if (!normalized.bookingType && criteria.bookingType?.$eq) {
    normalized.bookingType = criteria.bookingType.$eq;
  }

  const passenger = criteria.passenger;
  if (!normalized.passengerTypeIds?.length) {
    const passengerTypeIds =
      extractPassengerTypeIdsFromPassengerCriteria(passenger);
    if (passengerTypeIds?.length) {
      normalized.passengerTypeIds = passengerTypeIds;
    }
  }

  const bookingIds = asOperatorDescriptor(criteria.id)?.$in as
    number[] | undefined;
  if (!normalized.bookingIds?.length && bookingIds?.length) {
    normalized.bookingIds = bookingIds;
  }

  if (normalized.bookingIds?.length) {
    delete normalized.producedFrom;
    delete normalized.producedUntil;
  } else if (!normalized.producedFrom || !normalized.producedUntil) {
    const bookTimeRange = asOperatorDescriptor(criteria.bookTime)?.$between as
      [TDateISO?, TDateISO?] | undefined;
    if (bookTimeRange?.length) {
      if (!normalized.producedFrom && isValidDateTime(bookTimeRange[0])) {
        normalized.producedFrom = bookTimeRange[0];
      }
      if (!normalized.producedUntil && isValidDateTime(bookTimeRange[1])) {
        normalized.producedUntil = bookTimeRange[1];
      }
    }
  }

  if (!normalized.areaIds?.length && criteria.stationArea?.$any?.length) {
    normalized.areaIds = criteria.stationArea.$any as number[];
  }

  const shiftIds = asOperatorDescriptor(criteria.shiftId)?.$in as
    number[] | undefined;
  if (!normalized.shiftIds?.length && shiftIds?.length) {
    normalized.shiftIds = shiftIds;
  }

  const siteIds = asOperatorDescriptor(passenger?.siteId)?.$in as
    number[] | undefined;
  if (!normalized.siteIds?.length && siteIds?.length) {
    normalized.siteIds = siteIds;
  }

  const unitIds = asOperatorDescriptor(passenger?.unitId)?.$in as
    number[] | undefined;
  if (!normalized.unitIds?.length && unitIds?.length) {
    normalized.unitIds = unitIds;
  }

  const sectors = extractSectorsFromProductionCriteria(criteria);
  if (!normalized.sectors?.length && sectors?.length) {
    normalized.sectors = sectors;
  }

  if (!normalized.direction) {
    const direction = asOperatorDescriptor(criteria.direction);
    if (direction?.$eq) {
      normalized.direction =
        direction.$eq as IProductionFiltersMetadata["direction"];
    } else {
      const directionIn = direction?.$in as string[] | undefined;
      if (directionIn?.length) {
        normalized.direction = joinDirectionInValues(
          directionIn,
        ) as IProductionFiltersMetadata["direction"];
      }
    }
  }

  return normalized;
};

export const getProductionFiltersMetadataCacheKey = (
  filters: IProductionFiltersMetadataSource | undefined,
): string => {
  const normalized = normalizeProductionFiltersMetadata(filters);
  if (!normalized) {
    return "";
  }

  return JSON.stringify(normalized);
};

export const resolveProductionMetricsFilters = async (
  filters: IProductionFiltersMetadata,
  deps: IResolveProductionMetricsFiltersDeps,
): Promise<Record<string, ResolvedFilterValue>> => {
  const out: Record<string, ResolvedFilterValue> = {};
  const { getDropdownLabel, getDropdownLabels, t, formatDateTimeNormalized } =
    deps;

  if (filters.userId) {
    const label = await getDropdownLabel("user", filters.userId);
    out.userId = label;
  }
  for (const { key, entity } of PRODUCTION_METRICS_FILTER_ID_KEYS) {
    const ids = filters[key] as number[] | undefined;
    if (ids?.length) {
      if (entity === "booking") {
        out[key as string] = { entity, ids };
        continue;
      }

      const labels = await getDropdownLabels(entity, ids);
      out[key as string] = {
        entity,
        items: ids.map((id, i) => ({ label: labels[i] ?? String(id), id })),
      };
    }
  }
  if (filters.direction) {
    out.direction = t(`selectListLabel.${filters.direction}`);
  }
  if (filters.productionType?.length) {
    out.productionType = filters.productionType
      .map(type => t(`selectListLabel.${type}`))
      .join(PRODUCTION_LOGS_JOIN_DELIMITER);
  }

  if (hasBookingIdsInProductionFilters(filters)) {
    out.producedFrom = NO_VALUE_DISPLAY;
    out.producedUntil = NO_VALUE_DISPLAY;
  } else {
    if (filters.producedFrom) {
      out.producedFrom = formatProductionMetricsDate({
        dateIso: filters.producedFrom,
        formatDateTimeNormalized,
      });
    }
    if (filters.producedUntil) {
      out.producedUntil = formatProductionMetricsDate({
        dateIso: filters.producedUntil,
        formatDateTimeNormalized,
      });
    }
  }

  if (filters.action) {
    out.action = t(`booking.generalInfo.${filters.action}`);
  }
  if (filters.bookingType) {
    out.bookingType = t(`selectListLabel.${filters.bookingType}`);
  }

  if (filters.sectors?.length) {
    out.sectors = filters.sectors.join(PRODUCTION_LOGS_JOIN_DELIMITER);
  }

  if (filters.directionsService) {
    out.directionsService =
      PROVIDER_OPTIONS.find(
        option => option.value === filters.directionsService,
      )?.label ?? filters.directionsService;
  }

  if (filters.sendToSupplierAfterProduce !== undefined) {
    out.sendToSupplierAfterProduce = filters.sendToSupplierAfterProduce
      ? t("selectListLabel.Yes")
      : t("selectListLabel.No");
  }

  if (filters.isRejectUnassigned !== undefined) {
    out.isRejectUnassigned = filters.isRejectUnassigned
      ? t("selectListLabel.Yes")
      : t("selectListLabel.No");
  }

  if (filters.assignDocksDuringProduction !== undefined) {
    out.assignDocksDuringProduction = t(
      `selectListLabel.${filters.assignDocksDuringProduction}`,
    );
  }

  return out;
};

export const isIdFilterValue = (v: ResolvedFilterValue): v is IIdFilterValue =>
  typeof v === "object" &&
  v !== null &&
  "entity" in v &&
  ("items" in v || "ids" in v);

export const isIdsOnlyFilterValue = (
  v: IIdFilterValue,
): v is IIdsOnlyFilterValue => "ids" in v;

export const getProductionMetricsFiltersDisplayOrder = (
  t: (key: string) => string,
): TProductionMetricsFilterDisplayItem[] => [
  { key: "direction", label: t("booking.formLabel.direction") },
  { key: "action", label: t("rideProduction.metricsFilters.action") },
  {
    key: "productionType",
    label: t("rideProduction.metricsFilters.productionType"),
  },
  {
    key: "producedFrom",
    label: t("rideProduction.metricsFilters.producedFrom"),
  },
  {
    key: "producedUntil",
    label: t("rideProduction.metricsFilters.producedUntil"),
  },
  { key: "bookingType", label: t("produceButton.formLabel.bookingType") },
  { key: "sectors", label: t("produceButton.formLabel.sectors") },
  {
    key: "passengerTypeIds",
    label: t("rideProduction.metricsFilters.passengerTypeIds"),
  },
  {
    key: "bookingIds",
    label: t("rideProduction.metricsFilters.bookingIds"),
  },
  {
    key: "filterCourseIds",
    label: t("rideProduction.metricsFilters.filterCourseIds"),
  },
  { key: "userId", label: t("rideProduction.metricsFilters.userId") },
  { key: "siteIds", label: t("booking.generalInfo.sites") },
  { key: "areaIds", label: t("booking.generalInfo.areas") },
  { key: "unitIds", label: t("booking.generalInfo.units") },
  { key: "shiftIds", label: t("booking.generalInfo.shifts") },
  {
    key: "directionsService",
    label: t("produceButton.formLabel.directionsService"),
  },
  {
    key: "sendToSupplierAfterProduce",
    label: t("rideProduction.formLabel.sendToSupplierAfterProduce"),
  },
  {
    key: "isRejectUnassigned",
    label: t("rideProduction.formLabel.isRejectUnassigned"),
  },
  {
    key: "assignDocksDuringProduction",
    label: t("rideProduction.formLabel.assignDocksDuringProduction"),
  },
];

export const hasAnyProductionMetricsFilter = (
  filters: IProductionFiltersMetadata,
): boolean =>
  !!filters.userId ||
  (filters.siteIds?.length ?? 0) > 0 ||
  (filters.areaIds?.length ?? 0) > 0 ||
  (filters.unitIds?.length ?? 0) > 0 ||
  (filters.shiftIds?.length ?? 0) > 0 ||
  !!filters.rideTypeId ||
  !!filters.direction ||
  !!filters.producedFrom ||
  !!filters.producedUntil ||
  !!filters.action ||
  !!filters.bookingType ||
  (filters.sectors?.length ?? 0) > 0 ||
  !!filters.directionsService ||
  (filters.passengerTypeIds?.length ?? 0) > 0 ||
  (filters.bookingIds?.length ?? 0) > 0 ||
  (filters.filterCourseIds?.length ?? 0) > 0 ||
  filters.sendToSupplierAfterProduce !== undefined ||
  filters.isRejectUnassigned !== undefined ||
  filters.assignDocksDuringProduction !== undefined ||
  (filters.productionType?.length ?? 0) > 0;

export const mergeLogDataWithProductionFilters = (
  base: IRichLogData | undefined,
  productionFilters: IProductionFiltersMetadataSource | undefined,
): IRichLogData | undefined => {
  const mergedFiltersSource =
    productionFilters !== undefined && productionFilters !== null
      ? { ...base?.productionFilters, ...productionFilters }
      : base?.productionFilters;
  const normalizedFilters =
    normalizeProductionFiltersMetadata(mergedFiltersSource);

  if (normalizedFilters === undefined || normalizedFilters === null) {
    return base;
  }

  return base !== undefined && base !== null
    ? { ...base, productionFilters: normalizedFilters }
    : { productionFilters: normalizedFilters };
};
